import React, { useMemo, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHidden, RCCHookToast, RCCIcon, RCCServicesApi } from "react-common-components";
import Accordion from "src/components/Accordion";
import Loader from "src/components/Loader";
import Photo from "src/components/Photo";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import { AuditType, AuditSituation, GroupersAudit, IAudits, IAuditSelect, IGrouperOfAudits } from "../interface";
import { FilterButton, FilterButtonSelected } from "../styles";
import ListOfAudits from "./listOfAudits";

const GrouperOfAudits: React.FC<IGrouperOfAudits> = ({
	grouper,
	filters,
	groupersSelect,
	loadGroupers,
	layoutList
}) => {
	const {translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const [grouperData, setGrouperData] = useState(grouper);
	const [list, setList] = useState<IAudits[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;

	const [filterListSituation, setFilterListSituation] = useState<AuditSituation | undefined>(undefined);
	const [filterListType, setFilterListType] = useState<AuditType | undefined>(undefined);

	const handleClickGroupers = async (_e: any, expanded: boolean) => {
		if(!expanded)
			return;
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Audits/List",
				urlParameters: {
					...groupersSelect,
					id: grouperData.id || "",
					id2: grouperData.id2 || "",
					id3: grouperData.id3 || "",
					...filters,
					startDate: filters.startDate ? formatDate(filters.startDate) : "",
					endDate: filters.endDate ? formatDate(filters.endDate) : ""
				}
			});

			const listAudits = data as IAudits[];
			setList(listAudits);
			setLoading(false);	
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar as auditorias"), addToast);
			setLoading(false);	
		}
	};

	const getCounts = async () => {
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Audits/ListGroupers",
				urlParameters: {
					...groupersSelect,
					...filters,
					startDate: filters.startDate ? formatDate(filters.startDate) : "",
					endDate: filters.endDate ? formatDate(filters.endDate) : ""
				}
			});

			const newGroupData = data.filter((x: any) => x.id === grouperData.id)[0];
			setGrouperData(newGroupData);
			
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar contadores"), addToast);
		}
	};

	const onCallbackDelete = (id: number) => {
		setList((prevState) => prevState.filter((x) => x.adb001Id !== id));
		getCounts();
	};

	const onCallbackUpdate = async (id: number) => {
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Audits/Select",
				urlParameters: {
					id
				}
			});
			
			const audit = data as IAuditSelect;

			setList((prevState) => prevState.map((glist) => {
				if(glist.adb001Id === id)
					return audit;
				return glist;
			}));

			getCounts();

		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar auditoria, favor atualizar a pagina"), addToast);
		}
	};

	const filterAuditSituationOnClickCountBar = (situation: AuditSituation) => {
		let situationToUpdate: AuditSituation | undefined = situation;

		if(filterListSituation && filterListSituation === situation)
			situationToUpdate = undefined;

		setFilterListSituation(situationToUpdate);
	};

	const filterAuditTypeOnClickCountBar = (type: AuditType) => {
		let typeToUpdate: AuditType | undefined = type;

		if(filterListType && filterListType === type)
			typeToUpdate = undefined;

		setFilterListType(typeToUpdate);
	};

	const ListOfAuditsMemo = useMemo(() => (
		<>
			<ListOfAudits 
				data={list}
				layoutList={layoutList}
				onCallbackDelete={onCallbackDelete}
				onCallbackUpdate={onCallbackUpdate}
				loadGroupers={loadGroupers}
				filterListSituation={filterListSituation}
				filterListType={filterListType}
			/>
		</>
	), [filterListSituation, filterListType, list, layoutList, grouper]);

	return (
		<>
			{
				grouperData &&
				<Accordion 
					header={
						<>
							<RCCGrid 
								container
								item 
								alignItems="center"
								justifyContent="space-between"
							>
								<span style={{ display: "flex", alignItems: "center"}}>
							 {grouperData.image !== null && (
										<Photo 
											hover
											name={grouperData.imageName || ""}
											link={grouperData.image !== "" ? `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${grouperData.image}` : ""}
										/>
									)}
	
									{groupersSelect.group === GroupersAudit.TYPE ? translate(grouperData.title) : grouperData.title}
	
									{grouperData.title2 !== null && (
										<>
											<RCCIcon name="arrowRight" size={20} />
											{grouperData.title2}
										</>
									)}
									{grouperData.title3 !== null && (
										<>
											<RCCIcon name="arrowRight" size={20} />
											{grouperData.title3}
										</>
									)}
								</span>
								<div style={{ display: "flex"}}>
									<div style={{ display: "flex", flexDirection: "column"}}>
										<RCCGrid container justifyContent="flex-end">
											{grouperData.internal > 0 && (
												<RCCButton 
													color="black" 
													styleInline={filterListType && filterListType !== AuditType.INTERNAL ? FilterButtonSelected : FilterButton}
													onClick={(e) => { e.stopPropagation(); filterAuditTypeOnClickCountBar(AuditType.INTERNAL); }}
												>
													{grouperData.internal} <RCCHidden only="xs">{translate("Interna")}</RCCHidden> 
												</RCCButton>
											)}
											{grouperData.external > 0 && (
												<RCCButton 
													color="grey" 
													styleInline={filterListType && filterListType !== AuditType.EXTERNAL ? FilterButtonSelected : FilterButton}
													onClick={(e) => { e.stopPropagation(); filterAuditTypeOnClickCountBar(AuditType.EXTERNAL); }}
												>
													{grouperData.external} <RCCHidden only="xs">{translate("Externa")}</RCCHidden> 
												</RCCButton>
											)}
										</RCCGrid>
										<RCCGrid container justifyContent="flex-end">
											{grouperData.opened > 0 && (
												<RCCButton 
													color="purple" 
													styleInline={filterListSituation && filterListSituation !== AuditSituation.OPENED ? FilterButtonSelected : FilterButton}
													onClick={(e) => { e.stopPropagation(); filterAuditSituationOnClickCountBar(AuditSituation.OPENED); }}
												>
													{grouperData.opened} <RCCHidden only="xs">{grouperData.opened > 1 ? translate("abertas") : translate("aberta")}</RCCHidden> 
												</RCCButton>
											)}
											{grouperData.inAudit > 0 && (
												<RCCButton 
													color="blue" 
													styleInline={filterListSituation && filterListSituation !== AuditSituation.IN_AUDIT ? FilterButtonSelected : FilterButton}
													onClick={(e) => { e.stopPropagation(); filterAuditSituationOnClickCountBar(AuditSituation.IN_AUDIT); }}
												>
													{grouperData.inAudit} <RCCHidden only="xs">{translate("em auditoria")}</RCCHidden> 
												</RCCButton>
											)}
											{grouperData.finished > 0 && (
												<RCCButton 
													color="green" 
													styleInline={filterListSituation && filterListSituation !== AuditSituation.FINISHED ? FilterButtonSelected : FilterButton}
													onClick={(e) => { e.stopPropagation(); filterAuditSituationOnClickCountBar(AuditSituation.FINISHED); }}
												>
													{grouperData.finished} <RCCHidden only="xs">{grouperData.finished > 1 ? translate("concluídas") : translate("concluída")}</RCCHidden> 
												</RCCButton>
											)}
										</RCCGrid>
									</div>
									<div style={{ width: "120px", padding: "0 20px",  alignSelf: "center"}}>
										<Title color="purple" size="sm" styleInline="margin 0 4px;">{translate("Total")}: {grouperData.count}</Title>
									</div>
								</div>
	
							</RCCGrid>
						</>
					}
					onChange={(e : any, expanded: any) => handleClickGroupers(e, expanded)}
				>
					{loading ? (
						<Loader />
					): (
						<>
							{list ? (
								<>
									{ListOfAuditsMemo}
								</>     
							): (
								<RCCAlert 
									title={translate("Atenção")}
									description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
								/>
							)}
						</>
					)}
				</Accordion>
			}		
		</>

	);
};

export default GrouperOfAudits;
